import React, {Fragment, useState} from 'react';
import moment from 'moment';
import {withRouter} from 'react-router-dom';
import {connect, MapDispatchToProps, MapStateToProps} from 'react-redux';
import {Grid, TextField} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import {Modals} from "../../component";
import {resubmitCase} from "../../vendors/actions/case";
import {showResubmitCaseModal} from "../../vendors/actions/modals";
import {MIN_CASE_PRICE, MIN_COMMENT_LENGTH, PAYMENT_TYPES} from "../../vendors/utils/common";

/**
 * "Resubmit" - re-post a dead case as a new one instead of retyping it.
 *
 * The price is FREELY adjustable, up or down. The server refunds the parent in full and
 * makes a fresh charge on the clone, so a lower price is not a partial refund and there is
 * deliberately no minimum-increase rule to enforce here - only the ordinary creation
 * minimum, which the resubmit endpoint re-applies.
 *
 * Every field is optional to the endpoint and falls back to the parent's value; we pre-fill
 * from the parent so "resubmit unchanged" is one click.
 *
 * Split into two components for the same reason as rejectAttorney.tsx: this file is mounted
 * for the whole session, so the pre-filled state has to be initialised by an inner
 * component that mounts when the modal opens.
 */

const DATE_INPUT_FORMAT = 'YYYY-MM-DDTHH:mm';

// Case dates are stored and displayed as UTC wall-clock - moment.utc everywhere, never a
// zone conversion - and the create/edit forms post them back as a bare
// 'YYYY-MM-DDTHH:mm:ss' string (see DateFormSerializer). Same convention here.
const API_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const FREE_PAYMENT_TYPES = ['contingency', 'pro_bono'];

interface FormProps {
    caseData: any,
    paymentMethod?: string,
    onClose: () => void,
    onSubmit: (case_uid: string, data: any) => Promise<{ success: boolean, error?: string }>
}

const ResubmitCaseForm = (props: FormProps) => {
    const {caseData, paymentMethod, onClose, onSubmit} = props;
    const paymentType = caseData.payment_type;
    const isFree = FREE_PAYMENT_TYPES.indexOf(paymentType) !== -1;
    const needsDate = paymentType === 'single_appearance';

    const [price, setPrice] = useState(String(caseData.price || ''));
    const [comment, setComment] = useState(caseData.comment || '');
    const [date, setDate] = useState(caseData.date ? moment.utc(caseData.date).format(DATE_INPUT_FORMAT) : '');
    const [error, setError] = useState('');
    const [sending, setSending] = useState(false);

    const submit = async (): Promise<void> => {
        const numericPrice = Number(price);

        if (!isFree) {
            if (!numericPrice || Number.isNaN(numericPrice)) {
                return setError('Please enter a price.');
            }
            // Lower than the parent's price is fine - only the creation minimum applies.
            if (numericPrice < MIN_CASE_PRICE) {
                return setError(`Minimum price is $${MIN_CASE_PRICE}.`);
            }
            // Stripe needs something to bill. Without a card on file the server would
            // answer with a card error after having already created the new case.
            if (!paymentMethod) {
                return setError('Please add a payment card to your profile before resubmitting.');
            }
        }
        if (comment.trim().length < MIN_COMMENT_LENGTH) {
            return setError(`Please describe your case in at least ${MIN_COMMENT_LENGTH} characters.`);
        }
        if (needsDate && !date) {
            return setError('Please choose a date and time.');
        }

        setError('');
        setSending(true);
        const result = await onSubmit(caseData.case_uid, {
            price: isFree ? undefined : numericPrice,
            comment: comment.trim(),
            date: needsDate ? moment(date).format(API_DATE_FORMAT) : undefined,
            paymentMethod: isFree ? undefined : paymentMethod
        });
        setSending(false);
        if (!result.success) {
            setError(result.error || 'We could not resubmit this case. Please try again.');
        }
    };

    const description: any = <span className='m-0'>
        This posts your case again as a new request, so the lawyers on our network can submit
        fresh proposals.
        <span className='mt-2 d-block'>
            You can raise or lower the price&nbsp;&mdash; the original case is refunded in full and
            the new one is charged at the price you set here.
        </span>
    </span>;

    return (
        <Modals.Question open={true}
                         title={'Resubmit this case?'}
                         icon={'lightbulb'}
                         cancelable={{
                             buttonTitle: 'Never Mind',
                             action: onClose
                         }}
                         submit={{
                             buttonTitle: sending ? 'Resubmitting...' : 'Resubmit',
                             action: submit,
                             disabled: sending
                         }}
                         description={description}>
            <Fragment>
                <Grid container spacing={2}>
                    {!isFree && <Grid item xs={12} sm={needsDate ? 6 : 12}>
                        <TextField
                            id="resubmit-price"
                            label="Price ($)"
                            type="number"
                            value={price}
                            onChange={(e: any) => setPrice(e.target.value)}
                            fullWidth
                            variant="outlined"
                            inputProps={{
                                min: MIN_CASE_PRICE,
                                step: 1
                            }}
                            helperText={`${PAYMENT_TYPES[paymentType] || ''} - minimum $${MIN_CASE_PRICE}`}
                        />
                    </Grid>}
                    {needsDate && <Grid item xs={12} sm={isFree ? 12 : 6}>
                        <TextField
                            id="resubmit-date"
                            label="Date & Time"
                            type="datetime-local"
                            value={date}
                            onChange={(e: any) => setDate(e.target.value)}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>}
                    <Grid item xs={12}>
                        <TextField
                            id="resubmit-comment"
                            label="Description"
                            value={comment}
                            onChange={(e: any) => setComment(e.target.value)}
                            fullWidth
                            multiline
                            rows="5"
                            variant="outlined"
                            inputProps={{
                                maxLength: 4000
                            }}
                        />
                    </Grid>
                </Grid>
                {error && <MuiAlert className="mt-3 py-1" severity="error">{error}</MuiAlert>}
            </Fragment>
        </Modals.Question>
    );
};

interface OwnProps {
}

interface ConnectStateProps {
    Modals?: any,
    Case?: any,
    User?: any
}

interface ConnectDispatchProps {
    showResubmitCaseModal: (bool: boolean) => void
    resubmitCase: (case_uid: string, data: any) => Promise<{ success: boolean, error?: string }>
}

const ResubmitCase = (props: any) => {
    const {caseResubmitModal} = props.Modals;
    const {Case, User} = props;
    const caseData = Case?.caseData || Case;

    if (!caseResubmitModal || !caseData?.case_uid) return null;

    return <ResubmitCaseForm caseData={caseData}
                             paymentMethod={User?.paymentMethods?.id}
                             onClose={() => props.showResubmitCaseModal(false)}
                             onSubmit={props.resubmitCase}/>;
};

type ConnectStateMapper = MapStateToProps<ConnectStateProps, OwnProps>

const mapStateToProps: ConnectStateMapper = (state: any) => {
    const {Modals, Case, User} = state;
    return {Modals, Case, User}
};

type ConnectDispatchMapper = MapDispatchToProps<ConnectDispatchProps, OwnProps>

const mapDispatchToProps: ConnectDispatchMapper = (dispatch: any) => {
    return {
        showResubmitCaseModal: (data: boolean) => dispatch(showResubmitCaseModal(data)),
        resubmitCase: (case_uid: string, data: any) => dispatch(resubmitCase(case_uid, data))
    }
};

export {ResubmitCaseForm};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ResubmitCase));
